// Modal - wrapper around Bootstrap Modal. Elements matching [data-bs-modal]
// are shown on init (declarative "open on page load"); `create`/`getInstance`
// cover programmatic use. Behavioral port of v1 modules/Modal.js.
//
// v1 quirk dropped: lockBody/unlockBody defaulted their className to an
// undefined config key (adding a literal "undefined" class). Here the class
// is only touched when a className is passed in.

// Per-plugin import: pulls in only the modal plugin (and its data API), not
// the whole bootstrap bundle with every plugin's document-level listeners.
import BsModal from 'bootstrap/js/dist/modal';

export interface ModalConfig {
  backdrop: boolean | 'static';
  focus: boolean;
  keyboard: boolean;
}

const defaultConfig: ModalConfig = {
  backdrop: true,
  focus: true,
  keyboard: true,
};

const lockStyleProps = ['height', 'width', 'overflow', 'position'] as const;

export default class ModalWrapper {
  selector: string;

  config: ModalConfig;

  modals: Element[] = [];

  BsModal = BsModal;

  constructor(selector = '[data-bs-modal]', config: Partial<ModalConfig> = {}) {
    this.selector = selector;
    this.config = { ...defaultConfig, ...config };

    this._init();
  }

  private _init = (): void => {
    this.modals = [...document.querySelectorAll(this.selector)];

    this.modals.forEach(modal => {
      new this.BsModal(modal).show();
    });
  };

  /** Create a new Bootstrap modal instance. */
  create = (element: Element, config: Partial<ModalConfig> = {}): BsModal =>
    new this.BsModal(element, { ...this.config, ...config });

  /** Get an existing Bootstrap modal instance. */
  getInstance = (element: Element): BsModal | null =>
    this.BsModal.getInstance(element);

  destroy = (): void => {
    this.modals.forEach(modal => {
      this.BsModal.getInstance(modal)?.dispose();
    });
    this.modals = [];
  };

  /** Lock the body (and #page-container) to prevent scrolling. */
  static lockBody = (className?: string): void => {
    // Store current scroll position
    const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
    const { body } = document;
    const pageContainer = document.getElementById('page-container');

    // Save scroll position as data attribute
    body.setAttribute('data-lock-scrolltop', scrollTop.toString());

    const lockStyles = {
      height: '100%',
      width: '100%',
      overflow: 'hidden',
      position: 'fixed',
    } as const;

    Object.entries(lockStyles).forEach(([prop, value]) => {
      body.style.setProperty(prop, value);
    });

    if (className) {
      body.classList.add(className);
    }

    if (pageContainer) {
      Object.entries(lockStyles).forEach(([prop, value]) => {
        pageContainer.style.setProperty(prop, value);
      });

      // Maintain scroll position
      pageContainer.scrollTop = scrollTop;
    }

    // Ensure scroll position is maintained in the viewport
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollTop);
    });
  };

  /** Unlock the body to allow scrolling again. */
  static unlockBody = (className?: string): void => {
    const { body } = document;
    const scrollTop = parseInt(body.getAttribute('data-lock-scrolltop') || '0', 10);
    const pageContainer = document.getElementById('page-container');

    lockStyleProps.forEach(prop => {
      body.style.removeProperty(prop);
    });

    if (className) {
      body.classList.remove(className);
    }

    if (pageContainer) {
      lockStyleProps.forEach(prop => {
        pageContainer.style.removeProperty(prop);
      });
    }

    // Restore scroll position
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollTop);
    });
  };
}

export const { lockBody, unlockBody } = ModalWrapper;
